import { useCallback, useEffect, useRef, useState } from 'react'

export const getLiveTripPinKey = (userId, orgId) =>
	typeof userId === 'string' && userId && typeof orgId === 'string' && orgId
		? `livehq.footerPin.v1:${encodeURIComponent(userId)}:${encodeURIComponent(orgId)}`
		: null

const readPin = (key) => {
	if (!key || typeof window === 'undefined') return { pinnedTripId: null, canPersist: false }
	try {
		const storage = window.localStorage
		const id = storage.getItem(key)
		// Check write access too, so unavailable persistence has no pin control.
		const probeKey = `${key}:probe`
		storage.setItem(probeKey, '1')
		storage.removeItem(probeKey)
		if (id !== null && (!id.length || id.length > 200)) {
			storage.removeItem(key)
			return { pinnedTripId: null, canPersist: true }
		}
		return { pinnedTripId: id, canPersist: true }
	} catch {
		return { pinnedTripId: null, canPersist: false }
	}
}

export default function useLiveTripPin(userId, orgId, liveTrips) {
	const key = getLiveTripPinKey(userId, orgId)
	const [state, setState] = useState(() => ({ key, ...readPin(key) }))
	const validatedSnapshot = useRef(null)
	const canPersistPin = Boolean(key && state.key === key && state.canPersist)
	const pinnedTripId = canPersistPin ? state.pinnedTripId : null

	useEffect(() => {
		if (state.key !== key) setState({ key, ...readPin(key) })
	}, [key, state.key])

	const setPinnedTripId = useCallback((id) => {
		if (!canPersistPin || (id !== null && !liveTrips?.some((trip) => trip.id === id))) return
		try {
			if (id === null) window.localStorage.removeItem(key)
			else window.localStorage.setItem(key, id)
			setState({ key, pinnedTripId: id, canPersist: true })
		} catch {
			// Losing storage access leaves the footer usable and resumes rotation.
			setState({ key, pinnedTripId: null, canPersist: false })
		}
	}, [canPersistPin, key, liveTrips])

	useEffect(() => {
		if (!liveTrips || validatedSnapshot.current === liveTrips) return
		validatedSnapshot.current = liveTrips
		// Only a new successful API snapshot may invalidate a saved pin.
		// Loading, failed requests, and older snapshots in another tab cannot.
		if (pinnedTripId && !liveTrips.some((trip) => trip.id === pinnedTripId)) setPinnedTripId(null)
	}, [liveTrips, pinnedTripId, setPinnedTripId])

	useEffect(() => {
		if (!key) return undefined
		const handleStorage = (event) => {
			if (event.key === key || event.key === null) setState({ key, ...readPin(key) })
		}
		window.addEventListener('storage', handleStorage)
		return () => window.removeEventListener('storage', handleStorage)
	}, [key])

	return { pinnedTripId, canPersistPin, setPinnedTripId }
}
